import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { User } from "../../Models/user";
import {
  Features,
  UserRole,
  Steps,
  Languages,
} from "../../Enums/companyStatus";
import colors from "../../Constants/colors";
import { useCheckProgress, useCompanies } from "../../Query/useCompanies";
import { CompanyStatus } from "../../Enums/companyStatus";
import { CompanyInfo } from "../../Models/company";
import StepperComponent from "./StepperComponent";
import SelectEvaluationModel from "./SelectEvaluationModel";
import CategoryAndQuestionForm from "./CategoryAndQustionForm";
import EvaluateButton from "./EvaluateButton";
import SelectCompany from "./SelectCompany";
import LanguageSelect from "./LanguageSelect";
import {
  useEvaluationModel,
  useFetchEvaluation,
} from "../../Query/useEvaluation";
import { useCertifications } from "../../Query/useCertifications";
import { useQuestionLists } from "../../Query/useQuestions";
import { evaluationModel } from "../../Models/evaluationModel";
import { useCompanyContext } from "../../Contexts/SelectedCompanyContext";
import { useTranslation } from "react-i18next";
import ManageAnnexStep from "./Annex/ManageAnnexStep";
import ConfigOverrideStep from "./ConfigOverrideStep";
import { AppendixItem } from "../../Models/evaluation";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { handleAxiosError } from "../../Api/errorHandler";
import { AnnexDocument } from "../../Core/useAnnexActions";
import { useAnnexLocalStorage } from "../../Core/useAnnexLocalStorage";
import { applyOrder, createAppendix } from "../../Helpers/annexActions";
import { useTerminology } from "../../Core/useTerminology";
import color from "../../Constants/colors";

interface HomeComponentProps {
  user: User | undefined;
}

const HomeComponent: React.FC<HomeComponentProps> = ({ user }) => {
  const hasQuestionsPermission =
    hasPermission(Features.QUESTION_LISTS) || hasRole(UserRole.ADMIN);
  const hasAnnexPermission =
    hasPermission(Features.QA_INSIGHTS) || hasRole(UserRole.ADMIN);
  const hasConfigOverridePermission =
    hasPermission(Features.EVALUATION_CONFIG_OVERRIDE) ||
    hasRole(UserRole.ADMIN);
  const { t } = useTranslation("evaluationPage");
  const terminology = useTerminology();

  const steps = [
    t("steps.company", { entity: terminology.singular }),
    t("steps.evaluationModel"),
    t("steps.certificationCategory"),
    ...(hasQuestionsPermission ? [t("steps.questionList")] : []),
    ...(hasAnnexPermission ? [t("steps.annex")] : []),
    ...(hasConfigOverridePermission ? [t("steps.configOverride")] : []),
    t("steps.evaluation"),
  ];

  const questionListStepIndex = hasQuestionsPermission
    ? steps.findIndex((s) => s === t("steps.questionList"))
    : -1;

  const annexStepIndex = hasAnnexPermission
    ? steps.findIndex((s) => s === t("steps.annex"))
    : -1;

  const configOverrideStepIndex = hasConfigOverridePermission
    ? steps.findIndex((s) => s === t("steps.configOverride"))
    : -1;

  const evaluationStepIndex = steps.findIndex(
    (s) => s === t("steps.evaluation"),
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));
  const [company, setCompany] = useState<CompanyInfo>();
  const [selectedModelId, setSelectedModelId] = useState<number | "">("");
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [isEvaluateLoading, setIsEvaluateLoading] = useState(false);
  const [languageId, setLanguageId] = useState(
    user?.workspace?.language ?? Languages.GERMAN,
  );
  const [certificationId, setCertificationId] = useState("");
  const [questionListId, setQuestionListId] = useState("");
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [configOverrides, setConfigOverrides] = useState<
    Record<string, number | boolean | string>
  >({});
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { selectedCompanyId, setSelectedCompanyId } = useCompanyContext();
  const { syncAnnex, updateAnnex, syncPredefinedAnnex, updatePredefinedAnnex } =
    useAnnexLocalStorage();

  const [annex, setAnnex] = useState<AnnexDocument[]>([]);
  const orderedAnnex = applyOrder(annex);

  const [predefinedAnnex, setPredefinedAnnex] = useState<
    Record<number, AnnexDocument[]>
  >({});
  const [selectedPredefinedSection, setSelectedPredefinedSection] = useState<
    number | null
  >(null);

  const hasAnnexSynced = useRef(false);

  const { data: useEvaluationModelData, isLoading: useEvaluationModelLoading } =
    useEvaluationModel();
  const { colors: workspace } = useWorkspaceColors();
  const { data: companyResponse, refetch: refetchCompanies } = useCompanies(
    1,
    20,
    "name",
    "asc",
    "",
  );
  const { data: certifications } = useCertifications();
  const fetchEvaluateMutation = useFetchEvaluation();
  const location = useLocation();
  const initialCompanyId = location.state?.companyId ?? "";

  const { refetch: refetchCheckProgressData } = useCheckProgress();

  useEffect(() => {
    setSelectedModelId("");
    setCertificationId("");
    setQuestionListId("");
    setSelectedCompanyId("");
    setIsEvaluateLoading(false);
  }, [
    setSelectedModelId,
    setCertificationId,
    setQuestionListId,
    setSelectedCompanyId,
    setIsEvaluateLoading,
  ]);

  useEffect(() => {
    if (initialCompanyId && !selectedCompanyId) {
      setSelectedCompanyId(Number(initialCompanyId));
    }
  }, [initialCompanyId, selectedCompanyId, setSelectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId && !hasAnnexSynced.current) {
      const annexFromStorage = syncAnnex(selectedCompanyId);

      if (annexFromStorage) {
        setAnnex(annexFromStorage);
      }

      const predefinedAnnexFromStorage = syncPredefinedAnnex(selectedCompanyId);

      if (predefinedAnnexFromStorage) {
        setPredefinedAnnex(predefinedAnnexFromStorage);
      }

      hasAnnexSynced.current = true;
    }
  }, [selectedCompanyId, syncAnnex, syncPredefinedAnnex]);

  useEffect(() => {
    if (!hasAnnexSynced.current || !selectedCompanyId) {
      return;
    }

    updateAnnex(selectedCompanyId, annex);
    updatePredefinedAnnex(selectedCompanyId, predefinedAnnex);
  }, [
    annex,
    selectedCompanyId,
    updateAnnex,
    predefinedAnnex,
    updatePredefinedAnnex,
  ]);

  const modelOptions = useMemo<evaluationModel[]>(() => {
    if (!useEvaluationModelData || !user) return [];
    const accessibleIds = new Set(useEvaluationModelData.accessible_model_ids);
    return useEvaluationModelData.all_models.filter((model) =>
      accessibleIds.has(model.model_id),
    );
  }, [useEvaluationModelData, user]);

  const isStepCompleted = (stepIndex: number): boolean => {
    if (stepIndex === Steps.SELECT_COMPANY) {
      return !!selectedCompanyId;
    }
    if (stepIndex === Steps.SELECT_MODEL) {
      return selectedModelId !== "";
    }
    if (stepIndex === Steps.SELECT_CERTFICATION_CATEGORY) {
      return !!certificationId;
    }
    if (questionListStepIndex !== -1 && stepIndex === questionListStepIndex) {
      return !!questionListId;
    }
    if (annexStepIndex !== -1 && stepIndex === annexStepIndex) {
      // Annex step is completed only if it has been visited AND all previous steps are completed
      const previousStepsCompleted =
        !!selectedCompanyId &&
        selectedModelId !== "" &&
        !!certificationId &&
        (!hasQuestionsPermission || !!questionListId);
      return previousStepsCompleted && visitedSteps.has(annexStepIndex);
    }
    if (
      configOverrideStepIndex !== -1 &&
      stepIndex === configOverrideStepIndex
    ) {
      const previousStepsCompleted =
        !!selectedCompanyId &&
        selectedModelId !== "" &&
        !!certificationId &&
        (!hasQuestionsPermission || !!questionListId);
      return (
        previousStepsCompleted && visitedSteps.has(configOverrideStepIndex)
      );
    }
    if (evaluationStepIndex !== -1 && stepIndex === evaluationStepIndex) {
      // Evaluation step is completed only if it has been visited AND all previous steps are completed
      const previousStepsCompleted =
        !!selectedCompanyId &&
        selectedModelId !== "" &&
        !!certificationId &&
        (!hasQuestionsPermission || !!questionListId);
      return previousStepsCompleted && visitedSteps.has(evaluationStepIndex);
    }
    return false;
  };

  // Check if a step's requirements are met (for accessibility, not visual completion)
  const areStepRequirementsMet = (stepIndex: number): boolean => {
    if (stepIndex === Steps.SELECT_COMPANY) {
      return !!selectedCompanyId;
    }
    if (stepIndex === Steps.SELECT_MODEL) {
      return selectedModelId !== "";
    }
    if (stepIndex === Steps.SELECT_CERTFICATION_CATEGORY) {
      return !!certificationId;
    }
    if (questionListStepIndex !== -1 && stepIndex === questionListStepIndex) {
      return !!questionListId;
    }
    if (annexStepIndex !== -1 && stepIndex === annexStepIndex) {
      // Annex step requirements: all previous steps must be completed
      return (
        !!selectedCompanyId &&
        selectedModelId !== "" &&
        !!certificationId &&
        (!hasQuestionsPermission || !!questionListId)
      );
    }
    if (evaluationStepIndex !== -1 && stepIndex === evaluationStepIndex) {
      // Evaluation step requirements: all previous steps must be completed
      return (
        !!selectedCompanyId &&
        selectedModelId !== "" &&
        !!certificationId &&
        (!hasQuestionsPermission || !!questionListId)
      );
    }
    return false;
  };

  const isStepAccessible = (stepIndex: number): boolean => {
    // A step is accessible if all previous steps have their requirements met
    for (let i = 0; i < stepIndex; i++) {
      if (!areStepRequirementsMet(i)) {
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      // Always allow going to next step - the step components will handle validation
      setCurrentStep(nextStep);
      setVisitedSteps((prev) => new Set([...Array.from(prev), nextStep]));
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to accessible steps or the current step
    // isStepAccessible already checks if previous steps are completed
    if (isStepAccessible(stepIndex) || stepIndex === currentStep) {
      setCurrentStep(stepIndex);
      setVisitedSteps((prev) => new Set([...Array.from(prev), stepIndex]));
    }
  };

  const { data: questionLists } = useQuestionLists(
    certificationId ? Number(certificationId) : undefined,
  );

  useEffect(() => {
    if (!hasQuestionsPermission && questionLists?.question_lists?.length) {
      const defaultList = questionLists.question_lists.find(
        (list) => list.default,
      );
      setQuestionListId(
        (defaultList ?? questionLists.question_lists[0]).id.toString(),
      );
    }
  }, [hasQuestionsPermission, questionLists]);

  const getStepLabel = (stepIndex: number): string | null => {
    if (stepIndex === Steps.SELECT_COMPANY && selectedCompanyId) {
      const selectedCompany = companyResponse?.companies?.find(
        (company) => company.id === selectedCompanyId,
      );
      return selectedCompany?.name || null;
    }
    if (stepIndex === Steps.SELECT_MODEL && selectedModelId !== "") {
      const selectedModel = modelOptions?.find(
        (model) => model.model_id === selectedModelId,
      );
      return selectedModel?.model_name || null;
    }
    if (stepIndex === Steps.SELECT_CERTFICATION_CATEGORY && certificationId) {
      const selectedCert = certifications?.find(
        (cert) => cert.id.toString() === certificationId,
      );
      return selectedCert?.name || null;
    }
    if (
      questionListStepIndex !== -1 &&
      stepIndex === questionListStepIndex &&
      questionListId
    ) {
      const selectedQuestionList = questionLists?.question_lists.find(
        (list) => list.id.toString() === questionListId,
      );
      return selectedQuestionList?.name || null;
    }
    return null;
  };

  const handleSelectChange = (companyId: number) => {
    setSelectedCompanyId(companyId);
  };

  const handleModelChange = (modelId: number) => {
    setSelectedModelId(modelId);
  };

  const handleLanguageChange = (id: number) => {
    setLanguageId(id);
  };

  const handleAppendixCreation = (): AppendixItem[] => {
    if (!showPlaceholders) {
      return createAppendix(orderedAnnex);
    }

    if (selectedPredefinedSection) {
      const rawAnnex = predefinedAnnex[selectedPredefinedSection];
      return createAppendix(rawAnnex);
    }

    return [];
  };

  const handleConfigChange = (
    overrides: Record<string, number | boolean | string>,
  ) => {
    setConfigOverrides(overrides);
  };

  const handleEvaluateClick = async (
    questionListId: string,
    certificationId: string,
  ): Promise<void> => {
    setCompany(undefined);
    setIsButtonClicked(true);
    setIsEvaluateLoading(true);

    if (selectedModelId !== "" && selectedCompanyId) {
      const appendix = handleAppendixCreation();

      fetchEvaluateMutation.mutate(
        {
          selectedCompanyId,
          selectedModelId,
          questionListId,
          certificationId,
          languageId,
          appendix,
          configOverrides:
            Object.keys(configOverrides).length > 0
              ? configOverrides
              : undefined,
        },
        {
          onSuccess: async () => {
            const progress = await refetchCheckProgressData();
            const isInProgress = progress?.data?.in_progress;
            const newEvaluationId = progress?.data?.evaluation?.id;

            if (isInProgress && newEvaluationId) {
              sessionStorage.setItem(
                `hasRedirected_${newEvaluationId}`,
                "true",
              );
              navigate(`/evaluation_in_progress/${newEvaluationId}`);
            } else {
              setIsEvaluateLoading(false);
            }
          },
          onError: (error: unknown) => {
            setIsEvaluateLoading(false);
            const axiosError = error as {
              status?: number;
              evaluationId?: string;
            };

            if (axiosError.status === 409 && axiosError.evaluationId) {
              sessionStorage.setItem(
                `hasRedirected_${axiosError.evaluationId}`,
                "true",
              );
              navigate(`/evaluation_in_progress/${axiosError.evaluationId}`);
            }
            const errorMessage = handleAxiosError(error);
            enqueueSnackbar(
              `${t("evaluationPage.errorEvaluating", {
                entity: terminology.singular,
              })}: ${errorMessage}`,
              { variant: "error" },
            );
          },
        },
      );
    } else {
      setIsEvaluateLoading(false);
      enqueueSnackbar(`${t("evaluationPage.seleselectBothSnackbar")}`, {
        variant: "warning",
      });
    }
  };

  const handleResetEvaluation = () => {
    setCurrentStep(0);
    setVisitedSteps(new Set([0]));
    setCompany(undefined);
    setSelectedModelId("");
    setSelectedCompanyId("");
    setIsButtonClicked(false);
    setIsEvaluateLoading(false);
    setLanguageId(Languages.GERMAN);
    setCertificationId("");
    setQuestionListId("");
    setAnnex([]);
    setPredefinedAnnex([]);
    setShowPlaceholders(false);
    setSelectedPredefinedSection(null);
    setConfigOverrides({});
  };

  if (!user) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: colors.gray50 }}
    >
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: 3, pt: 4, pb: 3 }}>
        <StepperComponent
          currentStep={currentStep}
          steps={steps}
          onStepClick={handleStepClick}
          isStepCompleted={isStepCompleted}
          isStepAccessible={isStepAccessible}
          getStepLabel={getStepLabel}
          onPreviousStep={handlePreviousStep}
          onNextStep={handleNextStep}
          canGoPrevious={currentStep > 0}
          canGoNext={currentStep < steps.length - 1}
        />
        {currentStep === Steps.SELECT_COMPANY && (
          <SelectCompany
            selectedCompanyId={selectedCompanyId}
            handleSelectChange={handleSelectChange}
            companyResponse={companyResponse}
            isEvaluateLoading={isEvaluateLoading}
            onRefetchCompanies={refetchCompanies}
            onCompanySelected={() => {
              // Mark current step as visited and go to next step immediately
              setVisitedSteps(
                (prev) => new Set([...Array.from(prev), currentStep]),
              );
              if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);
                setVisitedSteps(
                  (prev) => new Set([...Array.from(prev), nextStep]),
                );
              }
            }}
          />
        )}
        {currentStep === Steps.SELECT_MODEL && (
          <SelectEvaluationModel
            selectedModelId={selectedModelId}
            modelOptions={modelOptions}
            handleModelChange={handleModelChange}
            isEvaluateLoading={isEvaluateLoading}
            useEvaluationModelLoading={useEvaluationModelLoading}
            onModelSelected={() => {
              // Mark current step as visited and go to next step immediately
              setVisitedSteps(
                (prev) => new Set([...Array.from(prev), currentStep]),
              );
              if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);
                setVisitedSteps(
                  (prev) => new Set([...Array.from(prev), nextStep]),
                );
              }
            }}
          />
        )}
        {(currentStep === Steps.SELECT_CERTFICATION_CATEGORY ||
          (questionListStepIndex !== -1 &&
            currentStep === questionListStepIndex)) && (
          <CategoryAndQuestionForm
            certificationId={certificationId}
            setCertificationId={setCertificationId}
            questionListId={questionListId}
            setQuestionListId={setQuestionListId}
            isEvaluateLoading={isEvaluateLoading}
            selectedModelId={selectedModelId}
            currentStep={currentStep}
            questionListStepIndex={questionListStepIndex}
            onCategorySelected={() => {
              // Mark current step as visited and go to next step immediately
              setVisitedSteps(
                (prev) => new Set([...Array.from(prev), currentStep]),
              );
              if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);
                setVisitedSteps(
                  (prev) => new Set([...Array.from(prev), nextStep]),
                );
              }
            }}
            onQuestionListSelected={() => {
              // Mark current step as visited and go to next step immediately
              setVisitedSteps(
                (prev) => new Set([...Array.from(prev), currentStep]),
              );
              if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1;
                setCurrentStep(nextStep);
                setVisitedSteps(
                  (prev) => new Set([...Array.from(prev), nextStep]),
                );
              }
            }}
          />
        )}
        {hasAnnexPermission && currentStep === annexStepIndex && (
          <ManageAnnexStep
            companyId={selectedCompanyId as number}
            showPlaceholders={showPlaceholders}
            setShowPlaceholders={setShowPlaceholders}
            annex={orderedAnnex}
            setAnnex={setAnnex}
            predefinedAnnex={predefinedAnnex}
            setPredefinedAnnex={setPredefinedAnnex}
            selectedPredefinedSection={selectedPredefinedSection}
            setSelectedPredefinedSection={setSelectedPredefinedSection}
            handleNextStep={handleNextStep}
            isStepAccessible={isStepAccessible}
            currentStep={currentStep}
          />
        )}
        {hasConfigOverridePermission &&
          currentStep === configOverrideStepIndex && (
            <ConfigOverrideStep
              configOverrides={configOverrides}
              onConfigChange={handleConfigChange}
            />
          )}
        {currentStep === evaluationStepIndex && (
          <>
            {(!company ||
              company.status === CompanyStatus.ERROR ||
              company.status === CompanyStatus.TIME_LIMIT_EXCEEDED) && (
              <EvaluateButton
                handleEvaluateClick={handleEvaluateClick}
                companyResponse={companyResponse}
                modelOptions={modelOptions}
                selectedCompanyId={selectedCompanyId}
                selectedModelId={selectedModelId}
                certificationId={certificationId}
                questionListId={questionListId}
                isButtonClicked={isButtonClicked}
                hasQuestionPermisson={hasQuestionsPermission}
              />
            )}
            <LanguageSelect
              languageId={languageId}
              onLanguageChange={handleLanguageChange}
              onReset={handleResetEvaluation}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default HomeComponent;
